import { useCallback, useEffect, useState } from "react";
import { FirmwareTable } from "../components/images/FirmwareTable";
import { FirmwareUpload } from "../components/images/FirmwareUpload";
import { ModelForm } from "../components/images/ModelForm";
import { ModelTable } from "../components/images/ModelTable";
import { PageHeader, Stat, Button } from "../ds";
import { api, type Model, type Firmware } from "../lib/api";

// Images & Modelle: fertige OpenWrt-Images hochladen und Router-Modelle pflegen.
// Der Server liest beim Upload aus dem Image, welche OpenWrt-Version und welches
// Gerät drinsteckt, und prüft das gegen das gewählte Modell.
export function ImagesPage() {
  const [models, setModels] = useState<Model[] | null>(null);
  const [firmware, setFirmware] = useState<Firmware[] | null>(null);
  // `null` = Formular zu, `{ model: null }` = neues Modell.
  const [editing, setEditing] = useState<{ model: Model | null } | null>(null);

  const reload = useCallback(() => {
    api
      .listModels()
      .then(setModels)
      .catch(() => setModels([]));
    api
      .listFirmware()
      .then(setFirmware)
      .catch(() => setFirmware([]));
  }, []);
  useEffect(reload, [reload]);

  async function remove(m: Model) {
    if (!confirm(`Modell "${m.name}" löschen?`)) return;
    await api.deleteModel(m.id);
    reload();
  }

  async function removeFirmware(f: Firmware) {
    if (!confirm(`Image "${f.version}" löschen? Die Datei wird mit entfernt.`)) return;
    await api.deleteFirmware(f.id);
    reload();
  }

  const newest = firmware?.[0]?.openwrtVersion || firmware?.[0]?.version;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Images & Modelle"
        subtitle="Fertige OpenWrt-Images hochladen — geprüft gegen das hinterlegte Router-Modell."
        actions={
          <Button variant="outline" iconName="plus" onClick={() => setEditing({ model: null })}>
            Neues Modell
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Modelle"
          value={models?.length ?? 0}
          iconName="module"
          loading={models === null}
        />
        <Stat
          label="Images"
          value={firmware?.length ?? 0}
          iconName="box"
          loading={firmware === null}
        />
        <Stat label="Neuestes" value={newest ?? "—"} iconName="tag" loading={firmware === null} />
        <Stat
          label="Mit Warnung"
          value={
            firmware?.filter((f) => f.checks?.some((c) => c.status === "mismatch")).length ?? 0
          }
          iconName="shield"
          loading={firmware === null}
        />
      </div>

      <FirmwareUpload models={models} onUploaded={reload} />

      {editing && (
        <ModelForm
          key={editing.model?.id ?? "neu"}
          model={editing.model}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      <FirmwareTable firmware={firmware} onDelete={(f) => void removeFirmware(f)} />

      <ModelTable
        models={models}
        onEdit={(m) => setEditing({ model: m })}
        onDelete={(m) => void remove(m)}
      />
    </div>
  );
}
